import type { PublicWheelSegment } from '@shared';

/**
 * Pure geometry for the wheel. No React, no DOM — everything here is maths on
 * numbers so slice layout can be unit-tested and, later, reused by the bot to
 * render the same wheel server-side.
 *
 * Angle convention: 0° is 12 o'clock (where the pointer sits) and angles grow
 * clockwise. A wheel rotated by `R` degrees puts the slice whose mid-angle is
 * `m` under the pointer when `R ≡ -m (mod 360)`.
 *
 * Slices are **always equal**. Sizing them by their odds would hand the player a
 * ruler for measuring the very numbers the owner controls separately — and it
 * takes this file's input down to a list of public segments, which by
 * construction has no odds to size by.
 */

/** The wheel is drawn in a fixed 0…100 viewBox and scaled by CSS. */
export const VIEWBOX = 100;
export const CENTER = VIEWBOX / 2;
/** Outer edge of the coloured slices, leaving room for the rim. */
export const OUTER_RADIUS = 45;
/** Inner edge — the hole the hub sits in. */
export const INNER_RADIUS = 12.5;
/** Where the label block is centred. Text runs along the radius from here. */
export const LABEL_RADIUS = 26;
/**
 * Radial room a label may use before it has to shrink. Sized so even a label at
 * its full length stops short of the icon band above it.
 */
export const LABEL_MAX_LENGTH = 21;
/** Where the slice icon sits — the outer band, clear of the text. */
export const ICON_RADIUS = 40;

export interface Slice {
  segment: PublicWheelSegment;
  /** Degrees from 12 o'clock, clockwise. */
  start: number;
  end: number;
  mid: number;
  /** Angular width in degrees. The same for every slice. */
  arc: number;
  /** The advertised odds for this slice (percent). Never the real ones. */
  displayChance: number;
  /** SVG path for the filled wedge. */
  path: string;
  /** Transform placing the label block along the slice's radius. */
  labelTransform: string;
  /** Transform placing the icon in the outer band, aligned with the label. */
  iconTransform: string;
  /** True when the label had to be flipped to stay readable. */
  flipped: boolean;
}

/** Point on a circle of radius `r`, `angle` degrees clockwise from 12 o'clock. */
export function polar(angle: number, r: number): { x: number; y: number } {
  const radians = ((angle - 90) * Math.PI) / 180;
  return { x: CENTER + r * Math.cos(radians), y: CENTER + r * Math.sin(radians) };
}

/** Donut wedge between two radii, swept clockwise from `start` to `end`. */
export function wedgePath(start: number, end: number, inner: number, outer: number): string {
  // A full circle can't be expressed as one arc — nudge the end so it renders.
  const sweep = end - start >= 360 ? 359.999 : end - start;
  const stop = start + sweep;
  const largeArc = sweep > 180 ? 1 : 0;

  const outerStart = polar(start, outer);
  const outerEnd = polar(stop, outer);
  const innerEnd = polar(stop, inner);
  const innerStart = polar(start, inner);

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outer} ${outer} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${inner} ${inner} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}`,
    'Z',
  ].join(' ');
}

/**
 * Lays the public segments out around the wheel in equal slices.
 *
 * The caller passes segments a player is allowed to see — filtering already
 * happened in `toPublicSegments`, so everything handed in gets a slice.
 */
export function buildSlices(segments: PublicWheelSegment[]): Slice[] {
  if (segments.length === 0) return [];

  const arc = 360 / segments.length;

  return segments.map((segment, index) => {
    const start = index * arc;
    const end = start + arc;
    const mid = start + arc / 2;

    // Labels run outward along the radius. Past 6 o'clock that would leave them
    // reading right-to-left, so they flip to run back toward the hub instead.
    const flipped = mid > 180;
    const rotation = flipped ? mid + 90 : mid - 90;
    const labelAnchor = polar(mid, LABEL_RADIUS);
    const iconAnchor = polar(mid, ICON_RADIUS);

    return {
      segment,
      start,
      end,
      mid,
      arc,
      displayChance: segment.displayChance,
      path: wedgePath(start, end, INNER_RADIUS, OUTER_RADIUS),
      labelTransform: `translate(${labelAnchor.x} ${labelAnchor.y}) rotate(${rotation})`,
      iconTransform: `translate(${iconAnchor.x} ${iconAnchor.y}) rotate(${rotation})`,
      flipped,
    };
  });
}

/**
 * Largest font size that keeps `text` inside the label band.
 *
 * Measuring text properly needs a DOM, which this file will never have. The
 * ratio below is the average advance width of the UI font at weight 800 — close
 * enough that short labels get the full size and long ones step down instead of
 * spilling over the rim.
 */
export function fitFontSize(text: string, ideal: number, min: number): number {
  const AVERAGE_GLYPH_WIDTH = 0.58;
  const chars = Math.max(1, text.length);
  const fits = LABEL_MAX_LENGTH / (chars * AVERAGE_GLYPH_WIDTH);
  return Math.max(min, Math.min(ideal, fits));
}

/**
 * How much a whole label block must shrink to stay inside its wedge.
 *
 * `fitFontSize` handles the radial direction, where every slice has the same
 * room. This handles the other one: a wedge narrower than about 45° is thinner
 * than a two-line block is tall, so past that point the block scales down with
 * the arc instead of spilling into its neighbours.
 */
export function labelScale(arc: number): number {
  const COMFORTABLE_ARC = 45;
  return Math.min(1, arc / COMFORTABLE_ARC);
}

/** Angles of the pegs between slices — what the pointer clicks against. */
export function pegAngles(slices: Slice[]): number[] {
  return slices.map((slice) => slice.start);
}

/**
 * Rotation that parks `mid` under the pointer, expressed as the smallest
 * forward (clockwise) travel from `from` after `turns` full revolutions.
 */
export function rotationForTarget(from: number, mid: number, turns: number): number {
  const current = ((from % 360) + 360) % 360;
  const target = (((360 - mid) % 360) + 360) % 360;
  const delta = (((target - current) % 360) + 360) % 360;
  // Always travel at least one extra turn so a near-zero delta still spins.
  return from + turns * 360 + (delta === 0 ? 360 : delta);
}

/**
 * How many pegs sit between two rotations. Drives the tick sounds and the
 * pointer's kick without needing to diff angles inside the render loop.
 */
export function pegsCrossed(fromRotation: number, toRotation: number, pegCount: number): number {
  if (pegCount <= 0) return 0;
  const step = 360 / pegCount;
  return Math.floor(toRotation / step) - Math.floor(fromRotation / step);
}
